/* eslint-disable no-console */
import axios from 'axios';

import {CLIMATE_DB_BASE_URL, CLIMATE_DB_ENDPOINT_JSON} from '../../../config';
import CRUDMethods from '../../../constants/CRUDConstants';

import {ensureAIToken} from './aiTokenAPI';

export interface GapAnalysisMetricRow {
  key: string;
  metric: string;
  average: {text: string; color: string};
  gap: string;
  insight: string;
  current_company_gap: string;
}

export interface PeerCompanyCell {
  entity_id: number;
  legal_name: string;
  display: string;
  verdict: string | null;
  is_subject: boolean;
  value?: number | null;
}

export interface PeerCompanyColumn {
  entity_id: number;
  legal_name: string;
  is_subject?: boolean;
}

export interface PeerMetricRow {
  key: string;
  metric: string;
  category?: string;
  unit?: string;
  polarity?: string;
  cells: PeerCompanyCell[];
  summary?: string;
  average: string;
}

export interface GapAnalysisPillarView {
  key: string;
  title: string;
  metrics: number;
  datasource: GapAnalysisMetricRow[] | PeerMetricRow[];
  isPeerComparison?: boolean;
  companies?: PeerCompanyColumn[];
}

export interface GapAnalysisView {
  pillars: GapAnalysisPillarView[];
  peerGroupName: string | null;
  companies?: PeerCompanyColumn[];
}

interface GapValue {
  display?: string | null;
}

interface GapBenchmark extends GapValue {
  n?: number;
}

interface GapDetail {
  verdict?: string | null;
  label?: string | null;
}

interface GapInsight {
  text?: string | null;
}

interface GapMetric {
  metric_id?: string;
  name?: string;
  company?: GapValue | null;
  benchmark?: GapBenchmark | null;
  gap?: GapDetail | null;
  insight?: GapInsight | null;
}

interface GapPillar {
  key?: string;
  title?: string;
  metric_count?: number;
  metrics?: GapMetric[];
}

interface GapAnalysisResponse {
  peer_group?: {name?: string | null};
  industry?: {name?: string | null} | null;
  pillars?: GapPillar[];
}

interface PeerApiCell {
  entity_id: number;
  value?: number | null;
  verdict?: string | null;
  display?: string;
  evidence?: string | null;
}

interface PeerApiRow {
  metric_id?: string;
  name?: string;
  data_type?: string;
  category?: string;
  unit?: string;
  polarity?: string;
  cells?: PeerApiCell[];
  summary?: string;
  benchmark?: {display?: string | null} | null;
  industry_average?: {display?: string | null} | string | null;
  peer_average?: string | null;
}

interface PeerApiPillar {
  key?: string;
  title?: string;
  rows?: PeerApiRow[];
}

interface PeerGapAnalysisResponse {
  status?: string;
  peer_group?: {name?: string | null};
  industry?: {name?: string | null} | null;
  columns?: PeerCompanyColumn[];
  pillars?: PeerApiPillar[];
}

const verdictColor = (verdict: string | null | undefined): string => {
  switch (verdict) {
    case 'better':
      return 'green';
    case 'worse':
      return 'red';
    case 'on_par':
      return 'blue';
    case 'context':
      return 'gold';
    default:
      return 'default';
  }
};

const displayText = (value: string | null | undefined): string => {
  const text = value?.trim() ?? '';

  return text === '' ? '—' : text;
};

const toMetricRow = (
  metric: GapMetric,
  index: number
): GapAnalysisMetricRow => {
  const metricId = metric.metric_id?.trim() ?? '';
  const metricName = metric.name?.trim() ?? '';

  return {
    key: metricId === '' ? String(index) : metricId,
    metric: metricName === '' ? 'Metric' : metricName,
    average: {
      text: displayText(metric.benchmark?.display),
      color: verdictColor(metric.gap?.verdict)
    },
    gap: displayText(metric.gap?.label),
    insight: displayText(metric.insight?.text),
    current_company_gap: displayText(metric.company?.display)
  };
};

const toPillars = (pillars: GapPillar[]): GapAnalysisPillarView[] => {
  return pillars.flatMap((pillar) => {
    const metrics = pillar.metrics ?? [];
    const title = pillar.title?.trim() ?? '';
    const pillarKey = pillar.key?.trim() ?? '';

    if (!title || !metrics.length) {
      return [];
    }

    return [
      {
        key: pillarKey === '' ? title.toLowerCase() : pillarKey,
        title,
        metrics: pillar.metric_count ?? metrics.length,
        datasource: metrics.map(toMetricRow)
      }
    ];
  });
};

const industryAverageFromRow = (row: PeerApiRow): string => {
  if (typeof row.industry_average === 'string') {
    return displayText(row.industry_average);
  }

  if (row.industry_average && typeof row.industry_average === 'object') {
    return displayText(row.industry_average.display);
  }

  if (row.peer_average) {
    return displayText(row.peer_average);
  }

  return displayText(row.benchmark?.display);
};

const toPeerMetricRow = (
  row: PeerApiRow,
  companies: PeerCompanyColumn[],
  index: number
): PeerMetricRow => {
  const metricId = row.metric_id?.trim() ?? '';
  const metricName = row.name?.trim() ?? '';
  const cells: PeerCompanyCell[] = [];
  const isQualitative = row.data_type === 'qualitative';

  companies.forEach((company) => {
    const cell = row.cells?.find((c) => c.entity_id === company.entity_id);
    // Qualitative display is only a level label; the evidence is the disclosure itself.
    const evidence = isQualitative ? cell?.evidence?.trim() ?? '' : '';
    cells.push({
      entity_id: company.entity_id,
      legal_name: company.legal_name,
      display: evidence !== '' ? evidence : cell?.display ?? '—',
      verdict: cell?.verdict ?? null,
      is_subject: company.is_subject ?? false,
      value: cell?.value ?? null
    });
  });

  return {
    key: metricId === '' ? String(index) : metricId,
    metric: metricName === '' ? 'Metric' : metricName,
    category: row.category?.trim(),
    unit: row.unit?.trim(),
    polarity: row.polarity?.trim(),
    cells,
    summary: row.summary?.trim(),
    average: industryAverageFromRow(row)
  };
};

const toPeerPillars = (
  pillars: PeerApiPillar[],
  companies: PeerCompanyColumn[]
): GapAnalysisPillarView[] => {
  return pillars.flatMap((pillar) => {
    const rows = pillar.rows ?? [];
    const title = pillar.title?.trim() ?? '';
    const pillarKey = pillar.key?.trim() ?? '';

    if (!title || !rows.length) {
      return [];
    }

    return [
      {
        key: pillarKey === '' ? title.toLowerCase() : pillarKey,
        title,
        metrics: rows.length,
        datasource: rows.map((row, index) =>
          toPeerMetricRow(row, companies, index)
        ),
        isPeerComparison: true,
        companies
      }
    ];
  });
};

/**
 * Company gap against its peer group for one reporting year.
 * `GET /api/gap-analysis?year=&mongo_company_id=`
 */
export const getGapAnalysis = async (
  companyId: string,
  selectedYear: string
): Promise<GapAnalysisView | null> => {
  const mongoCompanyId = companyId.trim();
  const year = selectedYear.trim();
  const token = await ensureAIToken();

  if (!mongoCompanyId || !year || !token) {
    return null;
  }

  try {
    const response = await axios.request<GapAnalysisResponse>({
      method: CRUDMethods.GET,
      url: `${CLIMATE_DB_BASE_URL}${CLIMATE_DB_ENDPOINT_JSON.gapAnalysis}`,
      params: {
        year,
        mongo_company_id: mongoCompanyId
      },
      headers: {Authorization: `Bearer ${token}`}
    });
    // The page's "Industry" label comes from `industry.name`.
    const peerGroupName = response.data?.industry?.name?.trim() ?? '';

    return {
      pillars: toPillars(response.data?.pillars ?? []),
      peerGroupName: peerGroupName === '' ? null : peerGroupName
    };
  } catch (err) {
    console.log('gap analysis err:', err);

    return null;
  }
};

/**
 * Peer companies gap analysis for one reporting year.
 * `GET /api/gap-analysis/peers?year=&mongo_company_id=&peer_group_id=`
 */
export const getPeerCompaniesGapAnalysis = async (
  companyId: string,
  selectedYear: string,
  peerGroupId?: string,
  peerIds?: number[]
): Promise<GapAnalysisView | null> => {
  const mongoCompanyId = companyId.trim();
  const year = selectedYear.trim();
  const token = await ensureAIToken();

  if (!mongoCompanyId || !year || !token) {
    return null;
  }

  const params: Record<string, string> = {
    year,
    mongo_company_id: mongoCompanyId
  };

  // At most one of peer_ids / default_count / peer_group_id may be sent —
  // two together is a 400. An explicit selection wins over the group.
  if (peerIds !== undefined && peerIds.length > 0) {
    params.peer_ids = peerIds.join(',');
  } else if (peerGroupId) {
    params.peer_group_id = peerGroupId;
  }

  try {
    const response = await axios.request<PeerGapAnalysisResponse>({
      method: CRUDMethods.GET,
      url: `${CLIMATE_DB_BASE_URL}${CLIMATE_DB_ENDPOINT_JSON.gapAnalysis}/peers`,
      params,
      headers: {Authorization: `Bearer ${token}`}
    });
    const peerGroupName = response.data?.industry?.name ?? '';
    const companies = response.data?.columns ?? [];
    const pillars = response.data?.pillars ?? [];

    return {
      pillars: toPeerPillars(pillars, companies),
      peerGroupName: peerGroupName === '' ? null : peerGroupName,
      companies
    };
  } catch (err) {
    console.log('peer gap analysis err:', err);

    return null;
  }
};
